import React from 'react';
import { renderStatusText } from '../CombatPlayerbar/statusWordGlyphs';
import './KeystoneHealAnimation.css';

/**
 * KeystoneHealAnimation — a compact demo of Metformin's PRN "Heal action", the
 * lactic lever that the guideline beat is NOT.
 *
 * MECHANIC (verified against the engine 2026-06-26, combat_heal_action.py): the
 * green Heal button (PlayerStatusRail, Met combats only) lets you drill a random
 * concept card mid-fight. A correct answer heals +5 HP base; ANY submit adds +1
 * lactic stack. Lactic then softens your outgoing damage (8%/stack, floor 50%) and
 * raises incoming damage (12%/stack). So the loop reads: tap Heal -> drill a card
 * -> +5 HP and +1 lactic -> the lactic meter ticks up one segment (the cost).
 *
 * Continuous gentle loop on the SAME 4.6s clock idiom as KeystoneBeatAnimation so
 * the two tooltip demos feel like one system: neutral -> Heal presses -> card check
 * draws -> outcome pips rise + a new lactic segment lights -> HOLD -> soft reset.
 * Pure CSS/SVG, no canvas. Reduced-motion: the loop stops and the base CSS (which
 * paints the answered frame: card drilled, pips visible, new segment lit) remains.
 */
export default function KeystoneHealAnimation() {
  return (
    <div
      className="kha"
      role="img"
      aria-label="The Heal action. Tap Heal to drill a card. A correct answer heals 5 HP and adds 1 lactic stack, and lactic softens your hits while raising the damage you take."
    >
      {/* Row 1 — tap Heal, drill a card */}
      <div className="kha__action">
        <span className="kha__btn">
          <svg className="kha__btn-icon" width="11" height="11" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
          Heal
        </span>
        <svg className="kha__arrow" width="14" height="10" viewBox="0 0 14 10" fill="none" aria-hidden="true">
          <path d="M1 5h11M8 1l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="kha__card">
          <span className="kha__card-label">Drill a card</span>
          <span className="kha__card-check" aria-hidden="true">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
              <path className="kha__card-check-path" d="M20 6L9 17L4 12"
                    stroke="currentColor" strokeWidth="2.5"
                    strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </span>
      </div>

      {/* Row 2 — outcome: a small heal AND a lactic stack, each as a faithful
          MINIATURE in-combat result chip (same chassis as
          .batch-result-pill--*): a green +5 HP heal chip and a red +1 lactic
          stack chip, each carrying the REAL renderStatusText status pill
          (glyph + word) INSIDE the chip, accent-matched to that resource's
          status color. */}
      <div className="kha__outcome" aria-hidden="true">
        <span className="kha__pip kha__pip--hp">{renderStatusText('+5 HP', { iconSize: 11 })}</span>
        <span className="kha__pip kha__pip--lactic">{renderStatusText('+1 lactic', { iconSize: 11 })}</span>
      </div>

      {/* Row 3 — the cost: the lactic meter ticks up; your hits soften, you take more */}
      <div className="kha__cost" aria-hidden="true">
        <span className="kha__meter">
          <i className="kha__seg is-on" />
          <i className="kha__seg is-on" />
          <i className="kha__seg kha__seg--new" />
          <i className="kha__seg" />
          <i className="kha__seg" />
        </span>
        <span className="kha__cost-label">hits soften, you take more</span>
      </div>
    </div>
  );
}
